import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";
import { ApiError, OpenAPI } from "@/client";
import { ApiError as AdminApiError, OpenAPI as OpenAPIAdmin } from "@/client-admin";
import { Toaster } from "@/components/ui/toaster";
import { checkIsSuperuser } from "./lib/utils";
import { routeTree } from "./routeTree.gen";

OpenAPI.BASE = import.meta.env.VITE_API_URL;
OpenAPIAdmin.BASE = import.meta.env.VITE_API_URL + "/api/v1/admin";

export const GRAFANA_AUTO_TRADER_MONITORING_URL =
  import.meta.env.VITE_GRAFANA_AUTO_TRADER_MONITORING_URL ||
  "https://monitoring.brainchildcommodity.dev/d/cex2ix98wcn40e/auto-trader-monitoring-new?orgId=1&from=now-6h&to=now&timezone=browser";

export const GRAFANA_AUTO_TRADER_LOGS_URL =
  import.meta.env.VITE_GRAFANA_AUTO_TRADER_LOGS_URL ||
  "https://monitoring.brainchildcommodity.dev/a/grafana-lokiexplore-app/explore/service/auto-trader-auto_trader-1/logs?patterns=%5B%5D&from=now-24h&to=now&var-lineFormat=&var-ds=deyxk5r41mqdce&var-filters=service_name%7C%3D%7Cauto-trader-auto_trader-1&var-fields=&var-levels=&var-metadata=&var-jsonFields=&var-patterns=&var-lineFilterV2=&var-lineFilters=&timezone=browser&var-all-fields=&displayedFields=%5B%5D&urlColumns=%5B%5D&visualizationType=%22logs%22&prettifyLogMessage=false&sortOrder=%22Descending%22&wrapLogMessage=false";

// Create a client
const handleAuthError = (error: unknown) => {
  if ((error instanceof ApiError || error instanceof AdminApiError) && error.status === 401) {
    localStorage.removeItem("access_token");
    OpenAPI.TOKEN = undefined;
    OpenAPIAdmin.TOKEN = undefined;
    window.location.href = "/login";
  }
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleAuthError,
  }),
  mutationCache: new MutationCache({
    onError: handleAuthError,
  }),
});

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
  },
});

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN_FRONTEND,
  integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function App() {
  const accessToken = localStorage.getItem("access_token");
  if (accessToken && typeof accessToken === "string" && checkIsSuperuser()) {
    OpenAPIAdmin.TOKEN = accessToken;
  }
  OpenAPI.TOKEN = accessToken ?? undefined;

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster />
    </QueryClientProvider>
  );
}

export default App;
